import { ref, shallowRef } from "vue";
import type { Post } from "@/types";
import type { AudioAd } from "@cc-consulting-nv/ccsdk";

const DEBUG_LOGS = import.meta.env.DEV;

// Global player state (singleton)
const currentTrack = shallowRef<Post | null>(null);
const queue = shallowRef<Post[]>([]);
const queueIndex = ref(0);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const volume = ref(80);
const isMuted = ref(false);
const queuePanelOpen = ref(false);
const lastNonZeroVolume = ref(80);

// Shuffle and repeat modes
const isShuffleEnabled = ref(false);
const repeatMode = ref<"off" | "all" | "one">("off");
const originalQueue = shallowRef<Post[]>([]); // Store original queue order for shuffle
const shuffledIndices = ref<number[]>([]); // Track shuffle order

// Radio station mode (non-interactive playback)
const isRadioStationMode = ref(false);

// When we intentionally change tracks (play/next/prev), auto-play the new src
const pendingAutoPlay = ref(false);

// Ad playback state
const isPlayingAd = ref(false);
const currentAd = shallowRef<AudioAd | null>(null);
const songsSinceLastAd = ref(0);
const SONGS_BEFORE_AD = 5; // Play ad after every 5 songs

// Crossfade configuration
const CROSSFADE_DURATION = 3000; // 3 seconds crossfade
const CROSSFADE_START_OFFSET = 3.5; // Start crossfade 3.5 seconds before track ends
const isCrossfading = ref(false);
const crossfadeProgress = ref(0); // 0-1 progress through crossfade

// Rating state for current track (shared across components)
// Key: post ULID
// Value:
// - number (0-5): user has rated (0 is an explicit "zero" rating)
// - null: user explicitly has no rating (e.g. removed)
// If a key is missing, it means "unknown" and consumers should fall back to post data.
const ratingCache = ref<Map<string, number | null>>(new Map());

// Audio element references (set by AudioPlayer component)
let audioElement: HTMLAudioElement | null = null;
let nextAudioElement: HTMLAudioElement | null = null; // For crossfade
let adAudioElement: HTMLAudioElement | null = null; // Dedicated for ads

// Media Session API support for lock screen controls
let mediaSession: MediaSession | null = null;
let positionUpdateInterval: number | null = null;
let lastPositionUpdateMs = 0;
let lastPositionSeconds = 0;

/**
 * Global audio player composable
 * Manages playback state across the application
 */
export function useAudioPlayer() {
  /**
   * Register the audio element from the AudioPlayer component
   */
  function setAudioElement(el: HTMLAudioElement | null) {
    audioElement = el;
  }

  /**
   * Register the next audio element for crossfade
   */
  function setNextAudioElement(el: HTMLAudioElement | null) {
    nextAudioElement = el;
  }

  /**
   * Get the next audio element for crossfade operations
   */
  function getNextAudioElement(): HTMLAudioElement | null {
    return nextAudioElement;
  }

  /**
   * Register the ad audio element
   */
  function setAdAudioElement(el: HTMLAudioElement | null) {
    adAudioElement = el;
  }

  /**
   * Get the ad audio element
   */
  function getAdAudioElement(): HTMLAudioElement | null {
    return adAudioElement;
  }

  /**
   * Get the main audio element
   */
  function getAudioElement(): HTMLAudioElement | null {
    return audioElement;
  }

  /**
   * Set crossfade state
   */
  function setCrossfading(value: boolean) {
    isCrossfading.value = value;
    if (!value) {
      crossfadeProgress.value = 0;
    }
  }

  /**
   * Update crossfade progress (0-1)
   */
  function setCrossfadeProgress(value: number) {
    crossfadeProgress.value = Math.max(0, Math.min(1, value));
  }

  /**
   * Play a track, optionally with a queue of tracks
   * Note: This resets radio station mode. Radio station playback should use
   * setRadioStationMode(true) after calling play() to re-enable it.
   */
  function play(post: Post, playQueue?: Post[]) {
    // Reset radio station mode when starting new playback
    // Radio stations explicitly set this to true after calling play()
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS) {
        console.log("[AudioPlayer] Resetting radio station mode for new playback");
      }
      isRadioStationMode.value = false;
    }

    // Filter to audio-only content
    const audioTypes = ["SONG", "PODCAST"];

    if (playQueue && playQueue.length > 0) {
      const audioQueue = playQueue.filter((p) =>
        audioTypes.includes(p.type || ""),
      );
      queue.value = audioQueue;
      queueIndex.value = audioQueue.findIndex(
        (p) => (p.ulid || p.id) === (post.ulid || post.id),
      );
      if (queueIndex.value === -1) queueIndex.value = 0;
    } else {
      queue.value = [post];
      queueIndex.value = 0;
    }

    // IMPORTANT: Set pendingAutoPlay BEFORE currentTrack
    // The watcher fires immediately when currentTrack changes,
    // so pendingAutoPlay must already be true at that point
    pendingAutoPlay.value = true;

    currentTrack.value = post;
    currentTime.value = 0;
    duration.value = 0;
  }

  /**
   * Toggle play/pause
   */
  async function togglePlay() {
    if (!audioElement) {
      console.warn("Audio element not available");
      return;
    }

    if (isPlaying.value) {
      audioElement.pause();
      // isPlaying will be set to false by onPause event handler
      return;
    }

    // On mobile devices (especially iOS), ensure audio is ready before playing
    // Check if src is set and audio has loaded enough data
    if (!audioElement.src || audioElement.src === '') {
      console.warn("Audio src not set, cannot play");
      isPlaying.value = false;
      return;
    }

    // Ensure audio has loaded enough data to play (especially important on mobile)
    // readyState: 0 = HAVE_NOTHING, 1 = HAVE_METADATA, 2 = HAVE_CURRENT_DATA, 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA
    if (audioElement.readyState < 2) {
      console.log("Audio not ready yet, loading...");
      // Load the audio and wait for it to be ready
      audioElement.load();
      
      // Wait for audio to be ready (with timeout)
      try {
        await new Promise<void>((resolve, reject) => {
          const onCanPlay = () => {
            audioElement!.removeEventListener('canplay', onCanPlay);
            audioElement!.removeEventListener('error', onError);
            resolve();
          };
          const onError = (e: Event) => {
            audioElement!.removeEventListener('canplay', onCanPlay);
            audioElement!.removeEventListener('error', onError);
            reject(e);
          };
          
          if (audioElement!.readyState >= 2) {
            resolve();
          } else {
            audioElement!.addEventListener('canplay', onCanPlay);
            audioElement!.addEventListener('error', onError);
            // Timeout after 3 seconds
            setTimeout(() => {
              audioElement!.removeEventListener('canplay', onCanPlay);
              audioElement!.removeEventListener('error', onError);
              reject(new Error('Audio load timeout'));
            }, 3000);
          }
        });
      } catch (loadErr) {
        console.error("Failed to load audio:", loadErr);
        isPlaying.value = false;
        return;
      }
    }

    // Don't set isPlaying optimistically - let the play() promise and onPlay event handle it
    // This prevents stuck state if play() fails (e.g., autoplay restrictions)
    try {
      await audioElement.play();
      // onPlay event handler will set isPlaying.value = true
    } catch (err) {
      console.error("Failed to play audio:", err);
      // Ensure state is correct if play fails
      isPlaying.value = false;
      
      // On mobile, provide more detailed error information
      if (err instanceof Error && audioElement) {
        console.error("Play error details:", {
          message: err.message,
          name: err.name,
          readyState: audioElement.readyState,
          src: audioElement.src,
          networkState: audioElement.networkState,
          error: audioElement.error
        });
      }
    }
  }

  /**
   * Play previous track in queue
   * Blocked during ad playback and radio station mode
   */
  function playPrevious() {
    // Block during ad playback
    if (isPlayingAd.value) {
      if (DEBUG_LOGS)
        console.log("[AudioPlayer] Cannot skip during ad playback");
      return;
    }
    // Block in radio station mode (non-interactive)
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS)
        console.log("[AudioPlayer] Cannot skip previous in radio station mode");
      return;
    }

    // Handle repeat-one by restarting the current track instead of moving back
    if (repeatMode.value === "one" && audioElement) {
      audioElement.currentTime = 0;
      currentTime.value = 0;
      if (isPlaying.value) {
        audioElement.play().catch(console.error);
      }
      return;
    }

    // At the start of the queue
    if (queueIndex.value <= 0) {
      // Wrap to end of queue when repeating all
      if (repeatMode.value === "all" && queue.value.length > 1) {
        pendingAutoPlay.value = isPlaying.value;
        queueIndex.value = queue.value.length - 1;
        currentTrack.value = queue.value[queueIndex.value];
      } else if (audioElement) {
        // Spotify-style: restart current song when no previous track
        audioElement.currentTime = 0;
        currentTime.value = 0;
        if (isPlaying.value) {
          audioElement.play().catch(console.error);
        }
      }
      return;
    }

    // Set pendingAutoPlay BEFORE changing currentTrack
    // Only auto-play if currently playing
    pendingAutoPlay.value = isPlaying.value;
    queueIndex.value--;
    currentTrack.value = queue.value[queueIndex.value];
  }

  /**
   * Skip to next track in queue
   * Blocked during ad playback
   * Note: Radio station mode allows skipping next (for unavailable tracks), but blocks manual skipping
   * @param forceAutoPlay - If true, force auto-play regardless of current playing state (used when song ends)
   */
  function skipNext(forceAutoPlay?: boolean) {
    // Block during ad playback
    if (isPlayingAd.value) {
      if (DEBUG_LOGS)
        console.log("[AudioPlayer] Cannot skip during ad playback");
      return;
    }
    // Note: We allow skipNext in radio mode for automatic track progression
    // Manual skipping is handled by the UI disabling the button

    // Safety check - ensure we have a queue
    if (!queue.value || queue.value.length === 0) {
      console.warn("[AudioPlayer] skipNext called with empty queue");
      return;
    }

    // Handle repeat one mode
    if (repeatMode.value === "one" && audioElement) {
      audioElement.currentTime = 0;
      if (forceAutoPlay || isPlaying.value) {
        audioElement.play().catch(console.error);
      }
      return;
    }

    // Handle end of queue
    if (queueIndex.value >= queue.value.length - 1) {
      // If repeat all is enabled, go back to start
      if (repeatMode.value === "all") {
        pendingAutoPlay.value = forceAutoPlay ?? isPlaying.value;
        queueIndex.value = 0;
        currentTrack.value = queue.value[0];
      }
      // If not repeating, do nothing (stay at last track)
      return;
    }

    // Normal case: move to next track in queue
    // Set pendingAutoPlay BEFORE changing currentTrack
    // Use forceAutoPlay if provided, otherwise auto-play only if currently playing
    pendingAutoPlay.value = forceAutoPlay ?? isPlaying.value;
    queueIndex.value++;
    currentTrack.value = queue.value[queueIndex.value];
  }

  /**
   * Seek to a specific time
   * Blocked during ad playback and radio station mode
   */
  function seek(time: number) {
    // Block seeking during ad playback
    if (isPlayingAd.value) {
      if (DEBUG_LOGS)
        console.log("[AudioPlayer] Cannot seek during ad playback");
      return;
    }

    // Block seeking in radio station mode (non-interactive)
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS)
        console.log("[AudioPlayer] Cannot seek in radio station mode");
      return;
    }

    if (!audioElement) return;
    audioElement.currentTime = time;
  }

  /**
   * Set volume (0-100)
   */
  function setVolume(value: number) {
    const clamped = Math.max(0, Math.min(100, value));
    volume.value = clamped;
    isMuted.value = clamped === 0;
    if (clamped > 0) {
      lastNonZeroVolume.value = clamped;
    }
    if (audioElement) {
      audioElement.volume = clamped / 100;
    }
  }

  /**
   * Toggle mute
   */
  function toggleMute() {
    if (isMuted.value) {
      setVolume(lastNonZeroVolume.value || 80);
    } else {
      // Remember the last non-zero volume so unmute restores properly
      if (volume.value > 0) {
        lastNonZeroVolume.value = volume.value;
      }
      setVolume(0);
    }
  }

  /**
   * Stop playback and clear current track
   */
  function stop() {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
    isPlaying.value = false;
    currentTrack.value = null;
    queue.value = [];
    queueIndex.value = 0;
  }

  /**
   * Add a track to the end of the queue
   * Blocked in radio station mode (queue is managed internally)
   */
  function addToQueue(post: Post) {
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS) console.log("[AudioPlayer] Cannot add to queue in radio station mode");
      return;
    }
    queue.value = [...queue.value, post];
  }

  /**
   * Add a track to play next (after current)
   * Blocked in radio station mode (queue is managed internally)
   */
  function insertNext(post: Post) {
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS) console.log("[AudioPlayer] Cannot insert next in radio station mode");
      return;
    }
    const newQueue = [...queue.value];
    newQueue.splice(queueIndex.value + 1, 0, post);
    queue.value = newQueue;
  }

  /**
   * Remove a track from the queue by index
   * Blocked in radio station mode (queue is managed internally)
   */
  function removeFromQueue(index: number) {
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS) console.log("[AudioPlayer] Cannot remove from queue in radio station mode");
      return;
    }
    if (index < 0 || index >= queue.value.length) return;

    const newQueue = [...queue.value];
    newQueue.splice(index, 1);

    // Adjust queue index if we removed a track before or at current position
    if (index < queueIndex.value) {
      queueIndex.value--;
    } else if (index === queueIndex.value) {
      // If we removed the current track, play the next one (or previous if at end)
      if (newQueue.length > 0) {
        const newIndex = Math.min(queueIndex.value, newQueue.length - 1);
        queueIndex.value = newIndex;
        currentTrack.value = newQueue[newIndex];
      } else {
        stop();
        return;
      }
    }

    queue.value = newQueue;
  }

  /**
   * Play a specific track in the queue by index
   */
  function playAtIndex(index: number) {
    if (index < 0 || index >= queue.value.length) return;

    queueIndex.value = index;
    currentTrack.value = queue.value[index];
    currentTime.value = 0;
    duration.value = 0;
  }

  /**
   * Clear the entire queue
   * Blocked in radio station mode (queue is managed internally)
   */
  function clearQueue() {
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS) console.log("[AudioPlayer] Cannot clear queue in radio station mode");
      return;
    }
    stop();
  }

  /**
   * Get the next track in queue without changing state
   * Used for crossfade preloading
   */
  function getNextTrack(): Post | null {
    // No crossfade during ads
    if (isPlayingAd.value) return null;

    // Handle repeat one mode - no crossfade needed, same track will repeat
    if (repeatMode.value === "one") return null;

    // Check if there's a next track
    if (queueIndex.value < queue.value.length - 1) {
      return queue.value[queueIndex.value + 1];
    }

    // If repeat all is enabled and we're at the end, next track is first
    if (repeatMode.value === "all" && queue.value.length > 0) {
      return queue.value[0];
    }

    return null;
  }

  /**
   * Reorder queue items (drag and drop)
   * Blocked in radio station mode (queue is managed internally)
   */
  function reorderQueue(fromIndex: number, toIndex: number) {
    if (isRadioStationMode.value) {
      if (DEBUG_LOGS) console.log("[AudioPlayer] Cannot reorder queue in radio station mode");
      return;
    }
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= queue.value.length) return;
    if (toIndex < 0 || toIndex >= queue.value.length) return;

    const newQueue = [...queue.value];
    const [movedItem] = newQueue.splice(fromIndex, 1);
    newQueue.splice(toIndex, 0, movedItem);

    // Adjust current index if needed
    if (fromIndex === queueIndex.value) {
      queueIndex.value = toIndex;
    } else if (fromIndex < queueIndex.value && toIndex >= queueIndex.value) {
      queueIndex.value--;
    } else if (fromIndex > queueIndex.value && toIndex <= queueIndex.value) {
      queueIndex.value++;
    }

    queue.value = newQueue;
  }

  /**
   * Toggle queue panel open/closed
   */
  function toggleQueuePanel() {
    queuePanelOpen.value = !queuePanelOpen.value;
  }

  /**
   * Open queue panel
   */
  function openQueuePanel() {
    queuePanelOpen.value = true;
  }

  /**
   * Close queue panel
   */
  function closeQueuePanel() {
    queuePanelOpen.value = false;
  }

  /**
   * Update playback state (called by AudioPlayer)
   */
  function updateState(state: {
    isPlaying?: boolean;
    currentTime?: number;
    duration?: number;
    currentTrack?: Post | null;
  }) {
    if (state.isPlaying !== undefined) isPlaying.value = state.isPlaying;
    if (state.currentTime !== undefined) currentTime.value = state.currentTime;
    if (state.duration !== undefined) duration.value = state.duration;
    if (state.currentTrack !== undefined)
      currentTrack.value = state.currentTrack;
  }

  /**
   * Get the cached rating for a post
   * @param postUlid - The post ULID to get rating for
   * @returns The cached value if present:
   * - number (0-5): rated (0 is explicit)
   * - null: explicitly not rated
   * - undefined: not in cache (unknown)
   */
  function getCachedRating(postUlid: string): number | null | undefined {
    if (!ratingCache.value.has(postUlid)) return undefined;
    return ratingCache.value.get(postUlid) ?? null;
  }

  /**
   * Update the cached rating for a post
   * Call this after successfully rating a post via API
   * @param postUlid - The post ULID
   * @param rating - The new rating (0-5), or null when removed
   */
  function setCachedRating(postUlid: string, rating: number | null) {
    ratingCache.value.set(postUlid, rating);
    // Trigger reactivity by creating a new Map
    ratingCache.value = new Map(ratingCache.value);
  }

  /**
   * Toggle shuffle mode
   * When enabling shuffle, randomizes the queue while keeping current track
   * When disabling shuffle, restores original queue order
   */
  function toggleShuffle() {
    isShuffleEnabled.value = !isShuffleEnabled.value;

    if (isShuffleEnabled.value) {
      // Save original queue if not already saved
      if (originalQueue.value.length === 0) {
        originalQueue.value = [...queue.value];
      }

      // Create shuffled order (Fisher-Yates shuffle)
      const indices = Array.from({ length: queue.value.length }, (_, i) => i);
      const currentIdx = queueIndex.value;

      // Remove current index and shuffle the rest
      indices.splice(currentIdx, 1);
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }

      // Put current track at the beginning
      indices.unshift(currentIdx);
      shuffledIndices.value = indices;

      // Apply shuffle to queue
      const shuffled = indices.map((i) => originalQueue.value[i]);
      queue.value = shuffled;
      queueIndex.value = 0; // Current track is now at index 0
    } else {
      // Restore original queue
      if (originalQueue.value.length > 0) {
        const currentTrackId =
          currentTrack.value?.ulid || currentTrack.value?.id;
        queue.value = [...originalQueue.value];

        // Find current track in original queue
        const originalIndex = originalQueue.value.findIndex(
          (p) => (p.ulid || p.id) === currentTrackId,
        );

        if (originalIndex !== -1) {
          queueIndex.value = originalIndex;
        }

        originalQueue.value = [];
        shuffledIndices.value = [];
      }
    }
  }

  /**
   * Toggle repeat mode
   * Cycles through: off -> all -> one -> off
   */
  function toggleRepeat() {
    if (repeatMode.value === "off") {
      repeatMode.value = "all";
    } else if (repeatMode.value === "all") {
      repeatMode.value = "one";
    } else {
      repeatMode.value = "off";
    }
  }

  /**
   * Set repeat mode directly
   * Used for auto-repeat on featured playlists/radio stations
   */
  function setRepeatMode(mode: "off" | "all" | "one") {
    repeatMode.value = mode;
  }

  /**
   * Setup Media Session API for lock screen controls
   * Shows track info, artwork, and progress on device lock screens and notification centers
   */
  function setupMediaSession() {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      if (DEBUG_LOGS) console.log("📱 Media Session API not available");
      return;
    }

    mediaSession = navigator.mediaSession;
    if (DEBUG_LOGS) console.log("📱 Media Session API initialized");

    // Set up action handlers for lock screen/notification controls
    try {
      mediaSession.setActionHandler("play", () => {
        if (DEBUG_LOGS) console.log("📱 Media Session: play action");
        if (audioElement) {
          audioElement.play().catch(console.error);
          isPlaying.value = true;
        }
      });

      mediaSession.setActionHandler("pause", () => {
        if (DEBUG_LOGS) console.log("📱 Media Session: pause action");
        if (audioElement) {
          audioElement.pause();
          isPlaying.value = false;
        }
      });

      mediaSession.setActionHandler("stop", () => {
        if (DEBUG_LOGS) console.log("📱 Media Session: stop action");
        stop();
      });

      mediaSession.setActionHandler("previoustrack", () => {
        if (DEBUG_LOGS) console.log("📱 Media Session: previous track action");
        playPrevious();
      });

      mediaSession.setActionHandler("nexttrack", () => {
        if (DEBUG_LOGS) console.log("📱 Media Session: next track action");
        skipNext();
      });

      // Seek action for scrubbing on lock screen
      mediaSession.setActionHandler("seekto", (details) => {
        if (typeof details.seekTime === "number") {
          if (DEBUG_LOGS)
            console.log("📱 Media Session: seek to", details.seekTime);
          seek(details.seekTime);
        }
      });

      // Optional: seek backward/forward actions
      mediaSession.setActionHandler("seekbackward", (details) => {
        const skipTime = details.seekOffset || 10;
        if (DEBUG_LOGS)
          console.log("📱 Media Session: seek backward", skipTime);
        seek(Math.max(0, currentTime.value - skipTime));
      });

      mediaSession.setActionHandler("seekforward", (details) => {
        const skipTime = details.seekOffset || 10;
        if (DEBUG_LOGS) console.log("📱 Media Session: seek forward", skipTime);
        seek(Math.min(duration.value, currentTime.value + skipTime));
      });
    } catch (error) {
      console.warn("📱 Failed to set up some Media Session handlers:", error);
    }
  }

  /**
   * Update Media Session metadata (shown on lock screen)
   * @param artist - Optional artist name to use (from AudioPlayer's displayArtist computed)
   */
  function updateMediaSession(artist?: string) {
    if (!mediaSession || !currentTrack.value) {
      return;
    }

    const track = currentTrack.value;

    // Get artwork URL
    const images = track.images as Array<{ url?: string }> | undefined;
    const artworkUrl = images?.[0]?.url;

    // Get track title - check multiple possible field names
    const trackTitle = track.title || (track as any).name || "Untitled";

    // Use provided artist or fallback to track data
    const trackArtist = artist || track.artist || "Unknown Artist";

    const trackAlbum = track.album || "";

    // Update metadata (shown on lock screen and notifications)
    mediaSession.metadata = new MediaMetadata({
      title: trackTitle,
      artist: trackArtist,
      album: trackAlbum,
      artwork: artworkUrl
        ? [
            { src: artworkUrl, sizes: "96x96", type: "image/jpeg" },
            { src: artworkUrl, sizes: "128x128", type: "image/jpeg" },
            { src: artworkUrl, sizes: "192x192", type: "image/jpeg" },
            { src: artworkUrl, sizes: "256x256", type: "image/jpeg" },
            { src: artworkUrl, sizes: "384x384", type: "image/jpeg" },
            { src: artworkUrl, sizes: "512x512", type: "image/jpeg" },
          ]
        : undefined,
    });

    // Update position state for non-live tracks
    updateMediaSessionPosition();
  }

  /**
   * Update Media Session playback state
   */
  function updateMediaSessionPlaybackState(
    state: "none" | "paused" | "playing",
  ) {
    if (!mediaSession) return;

    try {
      mediaSession.playbackState = state;
      if (DEBUG_LOGS) console.log("📱 Media Session playback state:", state);
    } catch (error) {
      console.warn("📱 Failed to update playback state:", error);
    }
  }

  /**
   * Update Media Session position state (throttled to prevent excessive calls)
   */
  function updateMediaSessionPosition() {
    if (!mediaSession || !audioElement) return;

    // Don't set position state for live streams
    if (currentTrack.value?.streamUrl) {
      return;
    }

    const audioDuration = audioElement.duration;
    const audioCurrentTime = audioElement.currentTime;

    // Only update if we have valid finite values
    if (!Number.isFinite(audioDuration) || !Number.isFinite(audioCurrentTime)) {
      return;
    }

    // Throttle updates to avoid excessive calls
    // Only update if position changed by at least 0.1 seconds or it's been 100ms
    const now = Date.now();
    const timeSinceLastUpdate = now - lastPositionUpdateMs;
    const positionDelta = Math.abs(audioCurrentTime - lastPositionSeconds);

    if (timeSinceLastUpdate < 100 && positionDelta < 0.1) {
      return; // Skip if updated recently and position hasn't changed much
    }

    try {
      mediaSession.setPositionState({
        duration: audioDuration,
        playbackRate: audioElement.playbackRate || 1.0,
        position: audioCurrentTime,
      });
      lastPositionUpdateMs = now;
      lastPositionSeconds = audioCurrentTime;
    } catch (error) {
      console.warn("📱 Failed to update position state:", error);
    }
  }

  /**
   * Start position tracking interval (100ms updates)
   */
  function startPositionTracking() {
    // Stop any existing interval
    stopPositionTracking();

    // Don't track position for live streams
    if (currentTrack.value?.streamUrl) {
      return;
    }

    // Update position state every 100ms for smoother Android background player updates
    // The updateMediaSessionPosition method has throttling to prevent excessive API calls
    positionUpdateInterval = window.setInterval(() => {
      updateMediaSessionPosition();
    }, 100);

    if (DEBUG_LOGS)
      console.log("📱 Started position tracking (100ms interval)");
  }

  /**
   * Stop position tracking interval
   */
  function stopPositionTracking() {
    if (positionUpdateInterval !== null) {
      clearInterval(positionUpdateInterval);
      positionUpdateInterval = null;
      if (DEBUG_LOGS) console.log("📱 Stopped position tracking");
    }
  }

  // ---------------------------------------------------------------------------
  // Ad Playback Methods
  // ---------------------------------------------------------------------------

  /**
   * Increment the song counter when a song finishes playing.
   * Called by AudioPlayer when a track ends naturally.
   * @returns true if an ad should play next
   */
  function incrementSongCounter(): boolean {
    songsSinceLastAd.value++;
    if (DEBUG_LOGS) {
      console.log(
        `[AudioPlayer] Song counter: ${songsSinceLastAd.value}/${SONGS_BEFORE_AD}`,
      );
    }
    return songsSinceLastAd.value >= SONGS_BEFORE_AD;
  }

  /**
   * Reset the song counter after an ad plays or on session start
   */
  function resetSongCounter(): void {
    songsSinceLastAd.value = 0;
    if (DEBUG_LOGS) console.log("[AudioPlayer] Song counter reset");
  }

  /**
   * Set the ad playback state
   * @param ad - The ad being played, or null when ad finishes/fails
   * @param completed - Whether the ad finished successfully (only relevant when ad is null)
   */
  function setAdPlayback(ad: AudioAd | null, completed = true): void {
    if (ad) {
      isPlayingAd.value = true;
      currentAd.value = ad;
      if (DEBUG_LOGS) console.log(`[AudioPlayer] Playing ad: ${ad.title}`);
    } else {
      isPlayingAd.value = false;
      currentAd.value = null;
      if (completed) {
        resetSongCounter();
        if (DEBUG_LOGS) console.log("[AudioPlayer] Ad finished successfully");
      } else {
        if (DEBUG_LOGS)
          console.log("[AudioPlayer] Ad playback cancelled/failed");
      }
    }
  }

  /**
   * Check if an ad should play based on the current song counter
   */
  function shouldPlayAd(): boolean {
    return songsSinceLastAd.value >= SONGS_BEFORE_AD;
  }

  /**
   * Set radio station mode (non-interactive playback)
   * When enabled, disables seeking, skipping previous, and queue management
   */
  function setRadioStationMode(enabled: boolean): void {
    isRadioStationMode.value = enabled;
    if (DEBUG_LOGS) {
      console.log(`[AudioPlayer] Radio station mode: ${enabled ? 'enabled' : 'disabled'}`);
    }
  }

  return {
    // State (readonly for consumers)
    currentTrack,
    queue,
    queueIndex,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    queuePanelOpen,
    pendingAutoPlay,
    ratingCache,
    isShuffleEnabled,
    repeatMode,

    // Ad state
    isPlayingAd,
    currentAd,
    songsSinceLastAd,

    // Crossfade state
    isCrossfading,
    crossfadeProgress,
    isRadioStationMode,
    CROSSFADE_DURATION,
    CROSSFADE_START_OFFSET,

    // Methods
    setAudioElement,
    setNextAudioElement,
    getAudioElement,
    getNextAudioElement,
    setAdAudioElement,
    getAdAudioElement,
    play,
    togglePlay,
    playPrevious,
    skipNext,
    seek,
    setVolume,
    toggleMute,
    stop,
    updateState,

    // Queue management
    addToQueue,
    insertNext,
    removeFromQueue,
    playAtIndex,
    clearQueue,
    reorderQueue,
    toggleQueuePanel,
    openQueuePanel,
    closeQueuePanel,
    getNextTrack,

    // Playback modes
    toggleShuffle,
    toggleRepeat,
    setRepeatMode,

    // Rating management
    getCachedRating,
    setCachedRating,

    // Media Session API
    setupMediaSession,
    updateMediaSession,
    updateMediaSessionPlaybackState,
    updateMediaSessionPosition,
    startPositionTracking,
    stopPositionTracking,

    // Ad playback
    incrementSongCounter,
    resetSongCounter,
    setAdPlayback,
    shouldPlayAd,

    // Crossfade control
    setCrossfading,
    setCrossfadeProgress,

    // Radio station mode
    setRadioStationMode,
  };
}

export type UseAudioPlayerReturn = ReturnType<typeof useAudioPlayer>;
